import type { Variants } from 'framer-motion';

export const MOTION_ATOM_NAMES = [
    'breathe',
    'blink',
    'look',
    'bounce',
    'shake',
    'auraPulse',
    'headTilt',
    'particleFloat',
    'spin',
    'squash',
    'stretch',
    'slowBlink',
] as const;

export type MotionAtomName = typeof MOTION_ATOM_NAMES[number];

export const MOTION_ATOMS: Record<MotionAtomName, Variants> = {
    breathe: {
        rest: { scale: 1 },
        active: { scale: [1, 1.035, 1], transition: { duration: 3.2, repeat: Infinity, ease: 'easeInOut' } },
    },
    blink: {
        rest: { scaleY: 1 },
        active: { scaleY: [1, 0.08, 1], transition: { duration: 0.16, repeat: Infinity, repeatDelay: 4.2 } },
    },
    look: {
        rest: { x: 0 },
        active: { x: [0, -1.5, 1.5, 0], transition: { duration: 1.6, repeat: Infinity, ease: 'easeInOut' } },
    },
    bounce: {
        rest: { y: 0 },
        active: { y: [0, -5, 0], transition: { duration: 0.65, ease: 'easeOut' } },
    },
    shake: {
        rest: { x: 0 },
        active: { x: [0, -1, 1, 0], transition: { duration: 0.08, repeat: Infinity } },
    },
    auraPulse: {
        rest: { opacity: 0.36 },
        active: { opacity: [0.26, 0.72, 0.26], transition: { duration: 2.4, repeat: Infinity, ease: 'easeInOut' } },
    },
    headTilt: {
        rest: { rotate: 0 },
        active: { rotate: [-2.5, 2.5, -2.5], transition: { duration: 0.7, repeat: Infinity, ease: 'easeInOut' } },
    },
    particleFloat: {
        rest: { opacity: 0.72, y: 0 },
        active: { opacity: [0, 1, 0], y: [0, -18], transition: { duration: 2.2, repeat: Infinity, ease: 'easeOut' } },
    },
    spin: {
        rest: { rotate: 0 },
        active: { rotate: 360, transition: { duration: 2, ease: 'linear', repeat: Infinity } },
    },
    squash: {
        rest: { scaleY: 1, scaleX: 1 },
        active: { scaleY: [1, 0.86, 1], scaleX: [1, 1.08, 1], transition: { duration: 1, ease: 'easeInOut' } },
    },
    stretch: {
        rest: { scaleY: 1, y: 0 },
        active: { scaleY: [1, 1.08, 1], y: [0, -1, 0], transition: { duration: 0.45, ease: 'easeOut' } },
    },
    slowBlink: {
        rest: { scaleY: 1 },
        active: { scaleY: [1, 0.08, 1], transition: { duration: 0.38, repeat: Infinity, repeatDelay: 5.2 } },
    },
};

export const PARAMETER_RAMPS = ['pupilSize', 'mouthCurve', 'auraOpacity', 'particleOpacity'] as const;
export type ParameterRampName = typeof PARAMETER_RAMPS[number];

export interface ComposedMotionRecipe {
    name: string;
    atoms: MotionAtomName[];
    ramps: ParameterRampName[];
}

export const PR1A_COMPOSED_MOTIONS: ReadonlyArray<ComposedMotionRecipe> = [
    { name: 'wakeStretch', atoms: ['stretch', 'slowBlink'], ramps: ['pupilSize'] },
    { name: 'waveGreet', atoms: ['bounce', 'headTilt'], ramps: ['mouthCurve'] },
    { name: 'eyeFlicker', atoms: ['blink', 'look'], ramps: ['pupilSize'] },
    { name: 'eyeFurrow', atoms: ['shake', 'squash'], ramps: ['mouthCurve'] },
    { name: 'fade', atoms: ['auraPulse'], ramps: ['auraOpacity', 'particleOpacity'] },
];
